import React, { useCallback, useEffect, useState } from "react";
import { pedidoBackend } from "../../registro/registro";
import Modal from "../../components/modal/Modal";
import Spinner from "../../components/spinner/Spinner";
import {
  Aviso,
  Boton,
  Detalle,
  Filtros,
  Kpi,
  Kpis,
  Paginacion,
  Panel,
  Tabla,
  TablaWrapper,
  Vacio,
} from "./panelStyles";

// Solicitudes de servicio: prospectos en zonas sin servicio. Sirve para ver
// dónde conviene instalar red y exportar todo a Excel.

const hoy = () => new Date().toISOString().slice(0, 10);
// Por defecto, desde el arranque del proyecto (que no se pierda ninguna).
const INICIO = "2026-01-01";

const fecha = (iso) =>
  iso
    ? new Date(iso).toLocaleString("es-AR", {
        timeZone: "America/Argentina/Buenos_Aires",
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const Campo = ({ k, v, ancho }) => {
  if (v == null || v === "") return null;
  return (
    <div className={ancho ? "campo ancho" : "campo"}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  );
};

const direccion = (s) =>
  [
    [s.calle, s.altura].filter(Boolean).join(" "),
    s.piso && `piso ${s.piso}`,
    s.dpto && `dpto ${s.dpto}`,
  ]
    .filter(Boolean)
    .join(", ") || "—";

const mensajeError = (e) =>
  e?.response?.data?.error || e?.message || "No se pudo consultar el panel";

const SolicitudesPanel = () => {
  const [filtros, setFiltros] = useState({ desde: INICIO, hasta: hoy(), provincia: "", q: "" });
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [paging, setPaging] = useState({ page: 1, totalPages: 1, total: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const params = useCallback(() => {
    const p = { desde: filtros.desde, hasta: filtros.hasta };
    if (filtros.provincia.trim()) p.provincia = filtros.provincia.trim();
    if (filtros.q.trim()) p.q = filtros.q.trim();
    return p;
  }, [filtros]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [l, z] = await Promise.all([
        pedidoBackend("get", "/panel/solicitudes", { params: { ...params(), page, pageSize: 50 } }),
        pedidoBackend("get", "/panel/solicitudes/zonas", { params: params() }),
      ]);
      setItems(l.data.data || []);
      setPaging(l.data.paging || { page: 1, totalPages: 1, total: 0 });
      setZonas(z.data.data || []);
    } catch (e) {
      setError(mensajeError(e));
      setItems([]);
      setZonas([]);
    } finally {
      setCargando(false);
    }
  }, [params, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setPage(1);
    setFiltros((f) => ({ ...f, [campo]: valor }));
  };

  const descargarExcel = async () => {
    setOcupado(true);
    setMensaje(null);
    try {
      const res = await pedidoBackend("get", "/panel/solicitudes/export.xlsx", {
        params: params(),
        responseType: "blob",
      });
      const nombre =
        /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] ||
        "solicitudes.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    } finally {
      setOcupado(false);
    }
  };

  const verDetalle = async (id) => {
    try {
      const { data } = await pedidoBackend("get", `/panel/solicitudes/${id}`);
      setDetalle(data.data);
    } catch (e) {
      setMensaje({ texto: mensajeError(e), error: true });
    }
  };

  return (
    <>
      <Panel>
        <Filtros>
          <label>
            Desde
            <input type="date" value={filtros.desde} onChange={(e) => cambiar("desde", e.target.value)} />
          </label>
          <label>
            Hasta
            <input type="date" value={filtros.hasta} onChange={(e) => cambiar("hasta", e.target.value)} />
          </label>
          <label>
            Provincia
            <input
              type="search"
              placeholder="Ej. Córdoba"
              value={filtros.provincia}
              onChange={(e) => cambiar("provincia", e.target.value)}
            />
          </label>
          <label>
            Buscar
            <input
              type="search"
              placeholder="Nombre, apellido, DNI o localidad"
              value={filtros.q}
              onChange={(e) => cambiar("q", e.target.value)}
            />
          </label>
          <Boton type="button" onClick={descargarExcel} disabled={ocupado || cargando}>
            ⬇ Descargar Excel
          </Boton>
        </Filtros>
        <Aviso>
          Prospectos en zonas sin servicio. El Excel trae el detalle y el ranking de zonas
          (para decidir dónde conviene instalar red).
        </Aviso>
        {mensaje && <Aviso $error={mensaje.error}>{mensaje.texto}</Aviso>}
        {error && <Aviso $error>{error}</Aviso>}
      </Panel>

      <Kpis>
        <Kpi>
          <strong>{paging.total}</strong>
          <span>Solicitudes en el período</span>
        </Kpi>
        <Kpi $color="var(--violetaOscuro)">
          <strong>{zonas.length}</strong>
          <span>Zonas distintas</span>
        </Kpi>
        {zonas[0] && (
          <Kpi $color="var(--naranjaOscuro)">
            <strong>{zonas[0].solicitudes}</strong>
            <span>
              Top: {zonas[0].localidad} ({zonas[0].provincia})
            </span>
          </Kpi>
        )}
      </Kpis>

      {zonas.length > 1 && (
        <TablaWrapper style={{ marginBottom: "1.25rem" }}>
          <Tabla $min="500px">
            <thead>
              <tr>
                <th>Provincia</th>
                <th>Localidad / barrio</th>
                <th>Solicitudes</th>
              </tr>
            </thead>
            <tbody>
              {zonas.map((z) => (
                <tr key={`${z.provincia}|${z.localidad}`}>
                  <td>{z.provincia}</td>
                  <td>{z.localidad}</td>
                  <td>{z.solicitudes}</td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        </TablaWrapper>
      )}

      <TablaWrapper>
        <Tabla $min="1050px">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cargó</th>
              <th>Prospecto</th>
              <th>DNI</th>
              <th>Teléfono</th>
              <th>Provincia</th>
              <th>Localidad / barrio</th>
              <th>Dirección</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {cargando && (
              <tr>
                <Vacio colSpan={9}>
                  <Spinner />
                </Vacio>
              </tr>
            )}
            {!cargando && items.length === 0 && (
              <tr>
                <Vacio colSpan={9}>No hay solicitudes con estos filtros.</Vacio>
              </tr>
            )}
            {!cargando &&
              items.map((s) => (
                <tr key={s.id}>
                  <td>{fecha(s.fechaSolicitud)}</td>
                  <td>
                    {s.promotorNombre || s.usuario}
                    <div style={{ color: "var(--grisMedio)", fontSize: "0.75rem" }}>{s.usuario}</div>
                  </td>
                  <td>
                    {[s.apellido, s.nombre].filter(Boolean).join(", ") || "—"}
                    {s.convertidaAt && (
                      <div
                        style={{
                          color: "#1e7d4f",
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          marginTop: "0.15rem",
                        }}
                      >
                        ✔ Convertida en venta{s.clienteId ? ` · N° ${s.clienteId}` : ""}
                      </div>
                    )}
                  </td>
                  <td>{s.dni || "—"}</td>
                  <td>{s.telefono || "—"}</td>
                  <td>{s.provincia}</td>
                  <td>{s.localidad}</td>
                  <td>{direccion(s)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Boton type="button" $secundario onClick={() => verDetalle(s.id)}>
                      Ver
                    </Boton>
                  </td>
                </tr>
              ))}
          </tbody>
        </Tabla>
        <Paginacion>
          <span>
            {paging.total} solicitudes · página {paging.page} de {paging.totalPages}
          </span>
          <Boton type="button" $secundario disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Anterior
          </Boton>
          <Boton
            type="button"
            $secundario
            disabled={page >= paging.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente →
          </Boton>
        </Paginacion>
      </TablaWrapper>

      <Modal isOpen={Boolean(detalle)} onClose={() => setDetalle(null)} size="wide">
        {detalle && (
          <Detalle>
            <div className="cabecera">
              <h3>
                Solicitud de {detalle.apellido}, {detalle.nombre}
              </h3>
            </div>
            <p className="sub">
              {fecha(detalle.fechaSolicitud)} · cargó {detalle.usuario}
              {detalle.promotorId ? ` · promotor ${detalle.promotorId}` : ""}
            </p>

            <h4>Prospecto</h4>
            <div className="grid">
              <Campo k="Apellido y nombre" v={[detalle.apellido, detalle.nombre].filter(Boolean).join(", ")} />
              <Campo k="DNI" v={detalle.dni} />
              <Campo k="Género" v={detalle.genero} />
              <Campo
                k="Teléfonos"
                v={(detalle.telefonos || []).map((t) => t.numero).join(" · ") || detalle.telefono}
              />
              <Campo k="Email" v={detalle.email} ancho />
            </div>

            <h4>Ubicación</h4>
            <div className="grid">
              <Campo k="Provincia" v={detalle.provincia} />
              <Campo k="Localidad / barrio" v={detalle.localidad} />
              <Campo k="Dirección" v={direccion(detalle)} ancho />
              <Campo k="Referencia" v={detalle.referencia} ancho />
            </div>

            <h4>Registro</h4>
            <div className="grid">
              <Campo k="IP" v={detalle.ip} />
              <Campo k="Referencia interna" v={detalle.id} ancho />
            </div>

            <details>
              <summary>Datos técnicos (JSON)</summary>
              <pre>{JSON.stringify(detalle.payload, null, 2)}</pre>
            </details>
          </Detalle>
        )}
      </Modal>
    </>
  );
};

export default SolicitudesPanel;
